'use client';

import { useEffect } from 'react';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import appLogger from '@/utils/logger';

/** Minimal JSON payload — no URLs, entries, or user-identifying data. */
function forwardToIngest(metric: Metric): void {
  const url = process.env.NEXT_PUBLIC_WEB_VITALS_INGEST_URL?.trim();
  if (!url) return;
  const body = JSON.stringify({
    name: metric.name,
    value: metric.value,
    id: metric.id,
    rating: metric.rating,
    navigationType: metric.navigationType,
  });
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }
  void fetch(url, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => {
    /* ingest is best-effort */
  });
}

function report(metric: Metric): void {
  appLogger.debug(`[web-vitals] ${metric.name}`, {
    value: metric.value,
    id: metric.id,
    rating: metric.rating,
  });
  forwardToIngest(metric);
}

/**
 * O3: optional RUM-style metrics when NEXT_PUBLIC_ENABLE_WEB_VITALS=true.
 * Optional NEXT_PUBLIC_WEB_VITALS_INGEST_URL POSTs the same fields (no PII).
 */
export default function WebVitalsReporter(): null {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_WEB_VITALS !== 'true') return;
    onCLS(report);
    onINP(report);
    onLCP(report);
    onFCP(report);
    onTTFB(report);
  }, []);
  return null;
}
